import { useState } from "react";
import { Link } from "react-router-dom";
import { CATEGORIES, CATEGORY_LABELS } from "../lib/constants";
import { useBrand } from "../context/BrandContext";
import { UnsubscribeModal } from "./UnsubscribeModal";

// A colophon rather than a sitemap: the wordmark set centre on the warmest
// sheet in the stack, a printer's rule, then the link columns beneath it.
export function Footer() {
    const brand = useBrand();
    const [unsubOpen, setUnsubOpen] = useState(false);

    return (
        <footer className="mt-24 border-t border-line bg-surface-raised" data-testid="footer">
            <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-20">
                {/* Colophon */}
                <div className="max-w-xl mx-auto text-center">
                    <span className="font-display text-3xl sm:text-[2.25rem] font-semibold leading-none text-foreground">
                        {brand.site_name}
                    </span>
                    <span className="block h-px w-16 mx-auto mt-5 bg-accent/70" />
                    {brand.tagline && (
                        <p className="mt-5 text-[15px] text-dim leading-relaxed">{brand.tagline}</p>
                    )}
                    <p className="mt-6 font-mono text-xs text-dim" data-testid="footer-contact-email">{brand.email}</p>
                    {brand.phone && <p className="mt-1.5 font-mono text-xs text-dim">{brand.phone}</p>}
                </div>

                <div className="rule mt-14" />

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-10 gap-y-12 mt-14">
                    {/* Sections */}
                    <div>
                        <h5 className="font-display text-lg font-semibold text-foreground">Sections</h5>
                        <ul className="mt-5 space-y-3">
                            {CATEGORIES.map((c) => (
                                <li key={c}>
                                    <Link to={`/category/${c}`} className="text-[15px] text-dim hover:text-accent transition-colors duration-200">
                                        {CATEGORY_LABELS[c]}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Company */}
                    <div>
                        <h5 className="font-display text-lg font-semibold text-foreground">Company</h5>
                        <ul className="mt-5 space-y-3">
                            <li><Link to="/advertise" data-testid="footer-advertise" className="text-[15px] text-dim hover:text-accent transition-colors duration-200">Advertise with us</Link></li>
                            <li><Link to="/archive" className="text-[15px] text-dim hover:text-accent transition-colors duration-200">Archive</Link></li>
                            <li><Link to="/privacy" data-testid="footer-privacy" className="text-[15px] text-dim hover:text-accent transition-colors duration-200">Privacy Policy</Link></li>
                            <li><Link to="/terms" data-testid="footer-terms" className="text-[15px] text-dim hover:text-accent transition-colors duration-200">Terms of Use</Link></li>
                        </ul>
                    </div>

                    {/* Newsletter */}
                    <div className="col-span-2 panel p-8">
                        <h5 className="font-display text-xl font-semibold text-foreground" data-testid="footer-newsletter-title">Newsletter</h5>
                        <p className="mt-3 text-[15px] text-dim leading-relaxed max-w-sm">
                            Get the latest financial news delivered to your inbox.
                        </p>
                        <Link
                            to="/register"
                            data-testid="footer-newsletter-subscribe"
                            className="mt-6 inline-block rounded-full bg-accent text-accent-foreground px-7 py-3 text-sm font-semibold shadow-card hover:shadow-glow transition-shadow duration-200"
                        >
                            Subscribe
                        </Link>
                        <button
                            onClick={() => setUnsubOpen(true)}
                            data-testid="footer-unsubscribe"
                            className="mt-5 block text-xs text-dim hover:text-neg transition-colors duration-200 link-underline"
                        >
                            Unsubscribe
                        </button>
                    </div>
                </div>

                <p className="mt-16 pt-8 border-t border-line text-center font-mono text-[11px] text-dim">
                    © {new Date().getFullYear()} {brand.site_name} · {brand.domain}
                </p>
            </div>
            <UnsubscribeModal open={unsubOpen} onOpenChange={setUnsubOpen} />
        </footer>
    );
}
